import { useEffect, useMemo, useState } from "react";
import { useAddresses } from "./useAddresses";

/**
 * Coordinates are rounded to about eleven metres before an address is asked for.
 *
 * A parked vehicle's position drifts by a few metres between polls, and without this each drift
 * would count as a new place to look up.
 */
const COORDINATE_DECIMALS = 4;
/**
 * How many looked-up places to hold on to.
 *
 * A whole shift of a moving fleet is a lot of distinct places, and this only exists to stop us
 * asking twice; the places learned longest ago are the least likely to be asked for again.
 */
const MAX_CACHED_PLACES = 5000;
/**
 * How many places one lookup may ask about.
 *
 * Reverse geocoding is slow, and the caller can hand over a whole zoomed-out fleet at once —
 * thousands of coordinates in a single call, which times out and answers nothing. Asked for in
 * batches instead: each answer makes the next batch the missing one, so everything is still
 * learned, just without one request that cannot finish.
 */
const MAX_PLACES_PER_LOOKUP = 200;

/** A rounded coordinate, as one string, so it can key a map. */
function placeKey(longitude: number, latitude: number): string {
  const factor = 10 ** COORDINATE_DECIMALS;
  return `${Math.round(longitude * factor) / factor},${Math.round(latitude * factor) / factor}`;
}

function coordinateOf(key: string): { x: number; y: number } {
  const [x, y] = key.split(",");
  return { x: Number(x), y: Number(y) };
}

/**
 * Turns positions into addresses, asking about each place only once.
 *
 * {@link useAddresses} takes the coordinates as part of its query key, so a caller watching things
 * that move — a live map, say — re-sends the whole set whenever any one of them moves, and leaves
 * another copy of the answer in the query cache each time. Here each place is looked up once and
 * kept: normally nothing is missing and no request goes out at all, and something arriving
 * somewhere new asks only about that one place.
 *
 * Positions are rounded first, so a parked vehicle's GPS drift is not a new place.
 *
 * @param positionById - Where each thing to be named is, as `[longitude, latitude]`.
 * @param enabled - Pass `false` while nothing is showing the addresses.
 * @returns The address of each entry that has one, under the same key it was passed in under.
 */
export function useAddressesByPosition(
  positionById: ReadonlyMap<string, [number, number]>,
  enabled = true,
): ReadonlyMap<string, string> {
  /** Place key to address; an empty string means "asked, and there is no address for it". */
  const [knownPlaces, setKnownPlaces] = useState<ReadonlyMap<string, string>>(new Map());

  const placeKeyById = useMemo(() => {
    const keys = new Map<string, string>();
    if (!enabled) {
      return keys;
    }
    for (const [id, [longitude, latitude]] of positionById) {
      keys.set(id, placeKey(longitude, latitude));
    }
    return keys;
  }, [positionById, enabled]);

  const missingCoordinates = useMemo(() => {
    const missing = new Set<string>();
    for (const key of placeKeyById.values()) {
      if (!knownPlaces.has(key)) {
        missing.add(key);
      }
    }
    // Sorted, because this array is part of the query key. In row order it is reshuffled every
    // time the list re-sorts — which, sorted by state or by duration, is every poll — and the
    // same batch would then be asked for again under a new key while the first is still in
    // flight.
    return [...missing].sort().slice(0, MAX_PLACES_PER_LOOKUP).map(coordinateOf);
  }, [placeKeyById, knownPlaces]);

  const { data: addresses, isPlaceholderData } = useAddresses(missingCoordinates, false, true);

  useEffect(() => {
    // While a new set of places is being looked up, the query hands back the answer to the
    // previous set — that is what keeps a list from blanking out mid-scroll. Zipping that answer
    // onto the places just asked about would file each of them under the wrong address, or under
    // none, and since a place counts as known once filed, the real answer would then be dropped.
    if (isPlaceholderData || missingCoordinates.length === 0 || !addresses) {
      return;
    }

    setKnownPlaces(previous => {
      const next = new Map(previous);
      missingCoordinates.forEach((coordinate, index) => {
        // Recorded even when nothing came back, or the same place would be asked about again on
        // every render for as long as a vehicle sits there.
        next.set(placeKey(coordinate.x, coordinate.y), addresses[index]?.formattedAddress ?? "");
      });

      for (const key of next.keys()) {
        if (next.size <= MAX_CACHED_PLACES) break;
        next.delete(key);
      }

      return next;
    });
  }, [addresses, isPlaceholderData, missingCoordinates]);

  return useMemo(() => {
    const addressById = new Map<string, string>();
    for (const [id, key] of placeKeyById) {
      const address = knownPlaces.get(key);
      if (address) {
        addressById.set(id, address);
      }
    }
    return addressById;
  }, [placeKeyById, knownPlaces]);
}
